import type React from 'react';

export type FloatingMenuAlign = 'left' | 'right';

const DROPDOWN_LAYER_FLOOR = 1500;
const MAX_CSS_Z_INDEX = 2147483647;

/**
 * Portalled menus leave the stacking context that originally contained their
 * trigger. Preserve that context by placing the menu one layer above the
 * highest positioned ancestor (including modal and drawer overlays), while
 * still honoring the shared dropdown layer on ordinary pages.
 */
export function getFloatingMenuZIndex(anchor: HTMLElement | null): number {
  if (!anchor || typeof window === 'undefined') return DROPDOWN_LAYER_FLOOR;
  let zIndex = DROPDOWN_LAYER_FLOOR;
  let ancestor: HTMLElement | null = anchor.parentElement;
  while (ancestor) {
    const parsed = Number.parseInt(window.getComputedStyle(ancestor).zIndex, 10);
    if (Number.isFinite(parsed)) {
      zIndex = Math.max(zIndex, Math.min(parsed + 1, MAX_CSS_Z_INDEX));
    }
    ancestor = ancestor.parentElement;
  }
  return zIndex;
}

/**
 * Positions a portalled dropdown against the button that opened it.
 *
 * The menu is rendered into `document.body` so an overflowing toolbar cannot
 * clip it, which means it has to be placed by hand: fixed to the viewport,
 * flipped above the anchor when there is no room below, and pulled back inside
 * the window when the anchor sits near an edge.
 *
 * Extracted from the order workspace, where three copies of this had grown.
 */
export function getFloatingMenuStyle(
  anchor: HTMLElement | null,
  minWidth: number,
  align: FloatingMenuAlign = 'left',
): React.CSSProperties {
  if (!anchor || typeof window === 'undefined') {
    return { position: 'fixed', left: 8, top: 8, minWidth, zIndex: DROPDOWN_LAYER_FLOOR };
  }
  const rect = anchor.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const estimatedHeight = 300;
  const openAbove = viewportHeight - rect.bottom < estimatedHeight;
  const rawLeft = align === 'right' ? rect.right - minWidth : rect.left;
  const left = Math.max(8, Math.min(rawLeft, viewportWidth - minWidth - 8));
  return {
    position: 'fixed',
    left,
    ...(openAbove
      ? { bottom: Math.max(8, viewportHeight - rect.top + 6) }
      : { top: Math.min(viewportHeight - 8, rect.bottom + 6) }),
    minWidth,
    maxHeight: 'min(360px, calc(100vh - 16px))',
    overflowY: 'auto',
    zIndex: getFloatingMenuZIndex(anchor),
  };
}
